/**
 * Feedback Modal Component
 * Simple feedback form with single text box
 */

import { useState } from "react";
import { X, Send, MessageSquare, AlertCircle, CheckCircle } from "lucide-react";
import { xhrPost, API } from "../utils/api";

export function FeedbackModal({ isOpen, onClose, prefillText = "", type = "feedback" }) {
  const [message, setMessage] = useState(prefillText);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'

  const handleSubmit = async () => {
    if (!message.trim()) return;
    
    setIsSubmitting(true);
    try {
      const { status: responseStatus } = await xhrPost(`${API}/feedback`, {
        message: message.trim(),
        type: type, // 'feedback' or 'bug_report'
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      
      if (responseStatus === 200 || responseStatus === 201) {
        setStatus('success');
        setTimeout(() => {
          onClose();
          setMessage("");
          setStatus(null);
        }, 1500);
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            {type === "bug_report" ? (
              <AlertCircle className="w-5 h-5 text-red-400" />
            ) : (
              <MessageSquare className="w-5 h-5 text-violet-400" />
            )}
            <h2 className="text-lg font-bold text-white">
              {type === "bug_report" ? "Report Issue" : "Send Feedback"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {status === 'success' ? (
            <div className="flex flex-col items-center py-8">
              <CheckCircle className="w-12 h-12 text-green-400 mb-3" />
              <p className="text-white font-medium">Thank you!</p>
              <p className="text-zinc-400 text-sm">Your message has been sent.</p>
            </div>
          ) : (
            <>
              <p className="text-zinc-400 text-sm mb-3">
                {type === "bug_report" 
                  ? "Describe what went wrong and we'll look into it."
                  : "We'd love to hear your thoughts!"
                }
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={type === "bug_report" 
                  ? "What happened? What were you trying to do?"
                  : "Share your feedback, ideas, or suggestions..."
                }
                className="w-full h-32 bg-black border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 resize-none text-sm"
                autoFocus
              />
              {status === 'error' && (
                <p className="text-red-400 text-xs mt-2">Failed to send. Please try again.</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {status !== 'success' && (
          <div className="px-5 pb-5">
            <button
              onClick={handleSubmit}
              disabled={!message.trim() || isSubmitting}
              className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Floating feedback button component
export function FeedbackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="feedback-btn"
      className="fixed bottom-20 right-4 sm:bottom-6 sm:right-24 w-12 h-12 bg-violet-600 hover:bg-violet-500 text-white rounded-full shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:scale-110 transition-all flex items-center justify-center z-40"
      title="Send Feedback"
    >
      <MessageSquare className="w-5 h-5" />
    </button>
  );
}

// Error with report option component
export function ErrorWithReport({ message, onReport }) {
  return (
    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 animate-fade-in">
      <p className="text-red-400 text-sm mb-2">{message}</p>
      <button
        onClick={onReport}
        className="text-xs text-zinc-500 hover:text-red-400 underline transition-colors"
      >
        Report this issue
      </button>
    </div>
  );
}

export default FeedbackModal;
